import React, { useState } from 'react';
import {
  IconButton,
  Drawer,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import {
  Person,
  Settings,
  Logout,
  AdminPanelSettings,
  SwapHoriz,
  Support,
  QrCodeScanner,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../features/auth/hooks/usePermissions';
import { SETTINGS_WRITE_SCOPES } from '../../../features/settings/settingsScopes';
import { SCANNER_STAFF_SCOPES } from '../../../features/inventory/scannerScopes';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../../../i18n/LanguageSwitcher';
import ProfilePicture from '../../common/ProfilePicture';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Styled Components matching NavigationList design
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 320,
    padding: theme.spacing(2, 0),
    boxSizing: 'border-box',
    border: 'none',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.palette.mode === 'dark'
      ? '-2px 0 8px rgba(0, 0, 0, 0.3)'
      : '-2px 0 8px rgba(0, 0, 0, 0.05)',
  },
}));

const DrawerContent = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
}));

const UserHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 1.5, 1.5, 1.5),
  marginBottom: theme.spacing(0.5),
}));

const UserInfoButton = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(1.5, 1.5),
  transition: 'all 0.15s ease-in-out',
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  cursor: 'pointer',
}));

const SectionDivider = styled(Box)(({ theme }) => ({
  height: 1,
  backgroundColor: theme.palette.divider,
  margin: theme.spacing(0.75, 2, 0.5, 2),
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  borderRadius: theme.spacing(0.5),
  margin: theme.spacing(0, 1.5),
  padding: theme.spacing(1.2, 1.5),
  minHeight: 36,
  transition: 'all 0.15s ease-in-out',
  position: 'relative',
  ...(active && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.contrastText,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
  }),
  ...(!active && {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  }),
}));

const ScrollableList = styled(List)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: 2,
  },
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 28,
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
  },
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.3,
  },
}));

const LogoutButtonContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1.5, 1.5, 1.5),
}));

const StyledLogoutButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(1.2, 2),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  minHeight: 36,
  transition: 'all 0.15s ease-in-out',
  border: `1px solid ${theme.palette.error.main}`,
  color: theme.palette.error.main,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1),
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
  },
}));

const LanguageSwitcherContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1.5, 1, 1.5),
}));

export default function UserDrawer() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions();
  const navigate = useNavigate();
  const { clearStack } = useNavigationStack();
  const [open, setOpen] = useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const navigateTo = (path) => {
    handleDrawerClose();
    clearStack();
    navigate(path);
  };

  const handleProfileClick = () => navigateTo('/profile');
  const handleSettingsClick = () => navigateTo('/settings');
  const handleAdminClick = () => navigateTo('/admin');
  const handleChangeUserClick = () => navigateTo('/auth/change-user');
  const handleScannerClick = () => navigateTo('/inventory/scanner');

  const handleSupportClick = () => {
    // TeamViewer QuickSupport download URL
    const teamViewerUrl = 'https://download.teamviewer.com/download/TeamViewerQS.exe';
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = teamViewerUrl;
    link.download = 'TeamViewerQS.exe';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    handleDrawerClose();
  };

  // Separate menu sections
  const changeUserSection = [
    {
      label: t('common:user_menu.change_user'),
      icon: <SwapHoriz />,
      onClick: handleChangeUserClick,
      path: '/auth/change-user',
    },
  ];

  const mainMenuItems = [
    {
      label: t('common:user_menu.profile'),
      icon: <Person />,
      onClick: handleProfileClick,
      path: '/profile',
    },
    ...(permissions.hasAnyScope(SETTINGS_WRITE_SCOPES) ? [{
      label: t('common:user_menu.settings'),
      icon: <Settings />,
      onClick: handleSettingsClick,
      path: '/settings',
    }] : []),
    ...(permissions.isAdmin ? [{
      label: t('common:user_menu.admin_panel'),
      icon: <AdminPanelSettings />,
      onClick: handleAdminClick,
      path: '/admin',
    }] : []),
    ...(permissions.hasAllScopes(SCANNER_STAFF_SCOPES) ? [{
      label: t('common:user_menu.scanner'),
      icon: <QrCodeScanner />,
      onClick: handleScannerClick,
      path: '/inventory/scanner',
    }] : []),
    {
      label: t('common:user_menu.remote_support'),
      icon: <Support />,
      onClick: handleSupportClick,
      path: null, // No path for this action
    },
  ];

  const isActive = (path) => {
    if (!path) return false;
    return window.location.pathname === path || window.location.pathname.startsWith(path);
  };

  const drawerContent = (
    <DrawerContent>
      {/* User Info Section */}
      <UserHeader onClick={handleProfileClick}>
        <UserInfoButton>
          <ProfilePicture
            profilePictureFileId={user?.profile_picture_file_id}
            name={user?.name}
            size={40}
            color={user?.color}
          />
          <Box sx={{ flex: 1, textAlign: 'left' }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{
                fontSize: '14px',
                lineHeight: 1.2,
                color: 'text.primary',
                mb: 0.25,
              }}
            >
              {user?.name || t('common:user_menu.user')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: '12px',
                lineHeight: 1.2,
              }}
            >
              {user?.department?.name || t('common:user_menu.department')}
            </Typography>
          </Box>
        </UserInfoButton>
      </UserHeader>

      <SectionDivider />

      {/* Change User Section */}
      <Box sx={{ mb: 1 }}>
        <List disablePadding>
          {changeUserSection.map((item, index) => (
            <ListItem key={index} disablePadding sx={{ mb: 0.125 }}>
              <StyledListItemButton
                active={isActive(item.path)}
                onClick={item.onClick}
              >
                <StyledListItemIcon>
                  {item.icon}
                </StyledListItemIcon>
                <StyledListItemText primary={item.label} />
              </StyledListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <SectionDivider />

      {/* Main Menu Items */}
      <ScrollableList disablePadding>
        {mainMenuItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ mb: 0.125 }}>
            <StyledListItemButton
              active={isActive(item.path)}
              onClick={item.onClick}
            >
              <StyledListItemIcon>
                {item.icon}
              </StyledListItemIcon>
              <StyledListItemText primary={item.label} />
            </StyledListItemButton>
          </ListItem>
        ))}
      </ScrollableList>

      {/* Language Switcher Section */}
      <LanguageSwitcherContainer>
        <SectionDivider sx={{ mb: 1 }} />
        <LanguageSwitcher />
      </LanguageSwitcherContainer>

      {/* Bottom Section - Logout */}
      <LogoutButtonContainer>
        <SectionDivider sx={{ mb: 1 }} />
        <StyledLogoutButton
          fullWidth
          startIcon={<Logout />}
          onClick={handleChangeUserClick}
        >
          {t('common:user_menu.logout')}
        </StyledLogoutButton>
      </LogoutButtonContainer>
    </DrawerContent>
  );

  return (
    <>
      {/* Avatar Button to open drawer */}
      <IconButton
        onClick={handleDrawerOpen}
        size="small"
        sx={{ ml: 0 }}
        aria-label={t('common:user_menu.open_user_menu')}
      >
        <ProfilePicture
          profilePictureFileId={user?.profile_picture_file_id}
          name={user?.name}
          size={32}
          color={user?.color}
        />
      </IconButton>

      {/* Right-side Drawer */}
      <StyledDrawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiBackdrop-root': {
            cursor: 'e-resize',
          }
        }}
      >
        {drawerContent}
      </StyledDrawer>
    </>
  );
}